import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AdminFormPage,
  saveErrorToast,
  useAdminFormPersistence,
} from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { useV2GetClientContactByIdQuery } from "@generated";
import { ContactForm } from "../ContactForm/ContactForm";
import { useContactMutations } from "../api";
import { emptyContactForm } from "../types";
import type { ContactFormValues } from "../types";

const FORM_ID = "contact-form";

export const ContactEdit = (): JSX.Element => {
  const navigate = useNavigate();
  const { id = "", contactId = "" } = useParams();
  const { update } = useContactMutations(id);
  const { data, isLoading, refetch } = useV2GetClientContactByIdQuery(
    {
      clientId: id,
      id: contactId,
    },
    { refetchOnMountOrArgChange: true }
  );
  const { toast, setToast, savedInitial, formKey, markSaved } =
    useAdminFormPersistence<ContactFormValues>();
  const [submitting, setSubmitting] = useState(false);

  const loadedInitial = useMemo<ContactFormValues>(() => {
    const attrs = data?.data?.attributes;
    if (!attrs) return emptyContactForm;
    return {
      name: attrs.name ?? "",
      title: attrs.title ?? "",
      email: attrs.email ?? "",
      phone: attrs.phone ?? "",
    };
  }, [data]);

  if (isLoading) return <ScopeStackSpinner />;

  const initialValues = savedInitial ?? loadedInitial;

  const handleSubmit = async (values: ContactFormValues) => {
    setSubmitting(true);
    try {
      await update(contactId, values);
      markSaved(values, `'${values.name}' saved`);
      // The generated API defines no cache tags, so the update can't
      // invalidate the cached GET. Refetch so the cache holds the
      // server-fresh record for the next time Edit is opened.
      refetch();
    } catch (err) {
      console.error("Failed to update contact", err);
      setToast(saveErrorToast(err, "contact"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <AdminFormPage
        title="Edit Contact"
        formId={FORM_ID}
        onCancel={() => navigate("../..", { relative: "path" })}
        isSubmitting={submitting}
      >
        <ContactForm
          key={formKey}
          id={FORM_ID}
          initialValues={initialValues}
          isSubmitting={submitting}
          onSubmit={handleSubmit}
        />
      </AdminFormPage>
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default ContactEdit;
